import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Alert, Box, Breadcrumbs, Button, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useRedirect } from 'react-admin';
import { useLocation } from 'react-router-dom';
import permissionsService, { UserPermissions } from '../../services/appointments/permissions';
import { CustomTabs } from '../common/CustomTabs/CustomTabs';
import '../common/tab-section.css';
import { TabPanel } from '../common/TabPanel/TabPanel';
import { AgentsCenter } from './agents/AgentsCenter';
import { AppointmentsCenter } from './appointments/AppointmentsCenter';
import { MembersList } from './members-list/MembersList';
import { MessagesCenter } from './messages/MessagesCenter';
import { MetricsCenter } from './metrics/MetricsCenter';
import { CallsCenter } from './phone-calls/PhoneCallsCenter';
import { TrackingNumbersCenter } from './tracking-numbers/TrackingNumbersCenter';
import { VoicemailCenter } from './voicemail/VoicemailCenter';
import { TeamsCenter } from './teams/TeamsCenter';
import { QueuesCenter } from './queues/QueuesCenter';

const TABS_BY_INDEX: { [index: number]: string } = {
  0: 'messages',
  1: 'calls',
  2: 'voicemails',
  3: 'agents',
  4: 'teams',
  5: 'queues',
  6: 'tracking-numbers',
  7: 'metrics',
  8: 'members-list',
  9: 'appointments',
};

export const CommunicationsCenter = () => {
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [loadingPermissions, setLoadingPermissions] = useState(true);
  const [permissionsError, setPermissionsError] = useState(false);
  const [permissionsAttempt, setPermissionsAttempt] = useState(0);

  const location = useLocation();
  const redirect = useRedirect();

  const handleChange = (event: React.SyntheticEvent, tabIndex: number) => {
    setCurrentTabIndex(tabIndex);
    redirect(`/communications/${TABS_BY_INDEX[tabIndex]}`);
  };

  // Fetch user permissions on mount
  useEffect(() => {
    const loadPermissions = async () => {
      try {
        setPermissionsError(false);
        const response = await permissionsService.fetchPermissions();
        if (response.success && response.data) {
          setPermissions(response.data);
        } else {
          // Previously this silently downgraded the user to non-admin, which
          // removed five tabs from the tab bar behind a generic warning toast.
          // People read that as "my access was revoked" rather than "the request
          // failed", so surface it as a retryable error instead.
          setPermissionsError(true);
        }
      } catch (error) {
        console.error('Error loading permissions:', error);
        setPermissionsError(true);
      } finally {
        setLoadingPermissions(false);
      }
    };

    loadPermissions();
  }, [permissionsAttempt]);

  useEffect(() => {
    const path = location.pathname.split('/')?.[2];

    if (!path) {
      redirect('/communications/messages');
      return;
    }

    const [index] = Object.entries(TABS_BY_INDEX).find(([index, tab]) => path === tab) || [null];

    if (!index) {
      return;
    }

    setCurrentTabIndex(Number(index));
  }, [location.pathname, redirect]);

  const CustomBreadcrumbs = ({
    title,
    currentPageTitle,
  }: {
    title: string;
    currentPageTitle: string;
  }) => (
    <Box role='presentation'>
      <Breadcrumbs aria-label='breadcrumb' separator={<NavigateNextIcon fontSize='small' />}>
        <Typography sx={{ display: 'flex', alignItems: 'center' }} color='#a0a0a0'>
          {title}
        </Typography>
        <Typography sx={{ display: 'flex', alignItems: 'center' }} color='text.primary'>
          {currentPageTitle}
        </Typography>
      </Breadcrumbs>
    </Box>
  );

  // Helper to create restricted content message
  const RestrictedContent = ({ sectionName }: { sectionName: string }) => (
    <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px' padding={4}>
      <Alert severity='info' sx={{ maxWidth: 600 }}>
        <Typography variant='h6' gutterBottom>
          Access Restricted
        </Typography>
        <Typography variant='body1'>
          The <strong>{sectionName}</strong> section is only available for managers and
          administrators.
        </Typography>
        <Typography variant='body2' sx={{ mt: 2 }}>
          If you believe you should have access to this section, please contact your system
          administrator.
        </Typography>
      </Alert>
    </Box>
  );

  // Determine which tabs to show based on permissions
  const getVisibleTabs = () => {
    const allTabs = [
      { label: 'SMS', index: 0, requiresAdmin: false },
      { label: 'Calls', index: 1, requiresAdmin: false },
      { label: 'Voicemail', index: 2, requiresAdmin: false },
      { label: 'Agents', index: 3, requiresAdmin: true },
      { label: 'Teams', index: 4, requiresAdmin: true },
      { label: 'Queues & Routes', index: 5, requiresAdmin: true },
      { label: 'Phone Numbers', index: 6, requiresAdmin: true },
      { label: 'Metrics', index: 7, requiresAdmin: true },
      { label: 'Members List', index: 8, requiresAdmin: false },
      { label: 'Appointments', index: 9, requiresAdmin: false },
    ];

    if (!permissions) return allTabs;

    return allTabs.filter((tab) => {
      if (tab.requiresAdmin) {
        return permissions.canAccessAdminSections;
      }
      return true;
    });
  };

  if (permissionsError) {
    return (
      <Box mt={4} display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
        <Alert
          severity='error'
          sx={{ maxWidth: 600 }}
          action={
            <Button
              color='inherit'
              size='small'
              onClick={() => setPermissionsAttempt((attempt) => attempt + 1)}
            >
              Retry
            </Button>
          }
        >
          <Typography variant='h6' gutterBottom>
            Could not load your permissions
          </Typography>
          <Typography variant='body2'>
            This is a problem loading the page, not a change to your access. Retry, or reload if it
            keeps happening.
          </Typography>
        </Alert>
      </Box>
    );
  }

  if (loadingPermissions) {
    return (
      <Box mt={4} display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
        <Typography>Loading permissions...</Typography>
      </Box>
    );
  }

  const visibleTabs = getVisibleTabs();

  return (
    <Box mt={4} className='tab-section-table'>
      <CustomTabs
        tabLabels={visibleTabs.map((tab) => ({ label: tab.label }))}
        value={currentTabIndex}
        onChange={handleChange}
      />
      <TabPanel value={currentTabIndex} index={0} key={'sms'} sx={{ padding: '14px 0' }}>
        <MessagesCenter
          breadcrumbs={<CustomBreadcrumbs title='Communications Center' currentPageTitle='SMS' />}
        />
      </TabPanel>

      <TabPanel value={currentTabIndex} index={1} key={'calls'} sx={{ padding: '14px 0' }}>
        <CallsCenter
          breadcrumbs={<CustomBreadcrumbs title='Communications Center' currentPageTitle='Calls' />}
        />
      </TabPanel>

      <TabPanel value={currentTabIndex} index={2} key={'voicemails'} sx={{ padding: '14px 0' }}>
        <VoicemailCenter
          breadcrumbs={
            <CustomBreadcrumbs title='Communications Center' currentPageTitle='Voicemail' />
          }
        />
      </TabPanel>

      <TabPanel value={currentTabIndex} index={3} key={'agents'} sx={{ padding: '14px 0' }}>
        {permissions?.canAccessAdminSections ? (
          <AgentsCenter
            breadcrumbs={
              <CustomBreadcrumbs title='Communications Center' currentPageTitle='Agents' />
            }
          />
        ) : (
          <RestrictedContent sectionName='Agents' />
        )}
      </TabPanel>

      <TabPanel value={currentTabIndex} index={4} key={'teams'} sx={{ padding: '14px 0' }}>
        {permissions?.canAccessAdminSections ? (
          <TeamsCenter
            breadcrumbs={
              <CustomBreadcrumbs title='Communications Center' currentPageTitle='Teams' />
            }
          />
        ) : (
          <RestrictedContent sectionName='Teams' />
        )}
      </TabPanel>

      <TabPanel value={currentTabIndex} index={5} key={'queues'} sx={{ padding: '14px 0' }}>
        {permissions?.canAccessAdminSections ? (
          <QueuesCenter
            breadcrumbs={
              <CustomBreadcrumbs title='Communications Center' currentPageTitle='Queues & Routes' />
            }
          />
        ) : (
          <RestrictedContent sectionName='Queues & Routes' />
        )}
      </TabPanel>

      <TabPanel
        value={currentTabIndex}
        index={6}
        key={'tracking-numbers'}
        sx={{ padding: '14px 0' }}
      >
        {permissions?.canAccessAdminSections ? (
          <TrackingNumbersCenter
            breadcrumbs={
              <CustomBreadcrumbs title='Communications Center' currentPageTitle='Phone Numbers' />
            }
          />
        ) : (
          <RestrictedContent sectionName='Phone Numbers' />
        )}
      </TabPanel>

      <TabPanel value={currentTabIndex} index={7} key={'metrics'} sx={{ padding: '14px 0' }}>
        {permissions?.canAccessAdminSections ? (
          <MetricsCenter
            breadcrumbs={
              <CustomBreadcrumbs title='Communications Center' currentPageTitle='Metrics' />
            }
          />
        ) : (
          <RestrictedContent sectionName='Metrics' />
        )}
      </TabPanel>

      <TabPanel value={currentTabIndex} index={8} key={'members-list'} sx={{ padding: '14px 0' }}>
        <MembersList
          breadcrumbs={
            <CustomBreadcrumbs title='Communications Center' currentPageTitle='Members List' />
          }
        />
      </TabPanel>

      <TabPanel value={currentTabIndex} index={9} key={'appointments'} sx={{ padding: '14px 0' }}>
        <AppointmentsCenter
          breadcrumbs={
            <CustomBreadcrumbs
              title='Communications Center'
              currentPageTitle='Upcoming Appointments'
            />
          }
        />
      </TabPanel>
    </Box>
  );
};
